import React, { useState, useEffect, useRef } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import './everyday-rewards.scss';
import { Form, Button, ButtonColor, ButtonVariant } from '@ampol/design-system';
import everydayRewardsLogo from '../../assets/Everyday_Rewards_logo.svg';
import EverydayRewardsInProgress from './everyday-rewards-in-progress';
import {
  getEverydayRewardsEnrolmentStatus,
  getEverydayRewardsLoginUri,
  everydayRewardsDisconnect,
} from '../../actions/everydayRewardsActions';
import EverydayRewardsCardNumber from './everyday-rewards-card-number';
import { trackCustomEvent } from 'utilities/app-insights';

/**
 * Everyday Rewards
 * @returns JSX Element
 */
const EverydayRewards = ({ accountId }) => {
  const dispatch = useDispatch();

  const isMounted = useRef(false);

  const everydayRewardsEnrolmentStatus = useSelector((state) => state.everydayRewardsStore.everydayRewardsEnrolmentStatus);
  const assignedGrandfatherClientNumber = useSelector((state) => state.userStore.assignedGrandfatherClientNumber);
  const loggedInUser = useSelector((state) => state.userStore.loggedInUser);
  const assignedAccountNumber = useSelector((state) => state.userStore.assignedAccountNumber);
  const readOnly = !useSelector((state) => state.userStore.canReadWriteAccounts);

  const everydayRewardsLoginUri = useSelector((state) => state.everydayRewardsStore.everydayRewardsLoginUri);
  const everydayRewardsLoginUriLoaded = useSelector((state) => state.everydayRewardsStore.everydayRewardsLoginUriLoaded);
  const everydayRewardsDisconnectLoading = useSelector((state) => state.everydayRewardsStore.everydayRewardsDisconnectLoading);
  const everydayRewardsDisconnectLoaded = useSelector((state) => state.everydayRewardsStore.everydayRewardsDisconnectLoaded);

  const [everydayRewardsLinked, setEverydayRewardsLinked] = useState(false);
  const [linkStatusLoading, setLinkStatusLoading] = useState(false);

  const [showEverydayRewardsHome, setShowEverydayRewardsHome] = useState(true);
  const [showEverydayRewardsInProgress, setShowEverydayRewardsInProgress] = useState(false);

  const [cardNumber, setCardNumber] = useState('');

  useEffect(() => {
    dispatch(getEverydayRewardsEnrolmentStatus(assignedGrandfatherClientNumber, accountId));
    dispatch(getEverydayRewardsLoginUri(`${assignedGrandfatherClientNumber};${accountId}`));
  }, []);

  useEffect(() => {
    if (everydayRewardsEnrolmentStatus.enrolmentStatus === 'Enrolled') {
      setEverydayRewardsLinked(true);
      setCardNumber(everydayRewardsEnrolmentStatus.loyaltyCardNumber);
      if (linkStatusLoading) {
        setLinkStatusLoading(false);
      }
    } else {
      setEverydayRewardsLinked(false);
    }
  }, [everydayRewardsEnrolmentStatus]);

  useEffect(() => {
    if (isMounted.current && everydayRewardsDisconnectLoaded) {
      setEverydayRewardsLinked(false);
    }

    isMounted.current = true;
  }, [everydayRewardsDisconnectLoaded]);

  const onLinkToEverydayRewardsClick = () => {
    window.open(everydayRewardsLoginUri, '_blank');
    setShowEverydayRewardsInProgress(true);
    setShowEverydayRewardsHome(false);
    window.dataLayer.push({
      event: 'Card_Portal_Accounts_Everyday_Rewards_Link',
      selected: true,
    });
    trackCustomEvent('Click Link EDR', loggedInUser, assignedAccountNumber, 'Clicked Link to initiate Everyday Rewards Card linking.', { selectedAccount: accountId });
  };

  const onUnlinkFromEverydayRewardsClick = () => {
    if (everydayRewardsDisconnectLoading) {
      return;
    }
    window.dataLayer.push({
      event: 'Card_Portal_Accounts_Everyday_Rewards_Unlink',
      button_click: true,
    });
    dispatch(everydayRewardsDisconnect(assignedGrandfatherClientNumber, accountId));
    trackCustomEvent('Click Unlink EDR', loggedInUser, assignedAccountNumber, 'Clicked Unlink Everyday Rewards Cards', { selectedAccount: accountId });
  };

  const onInProgressCompleteClick = () => {
    dispatch(getEverydayRewardsEnrolmentStatus(assignedGrandfatherClientNumber, accountId, true));
    setShowEverydayRewardsInProgress(false);
    setShowEverydayRewardsHome(true);
    setLinkStatusLoading(true);
    trackCustomEvent('Click Done EDR', loggedInUser, assignedAccountNumber, 'Clicked Done to complete Everyday Rewards Card linking.', { selectedAccount: accountId });
  };

  return <>
    {showEverydayRewardsHome && everydayRewardsLoginUriLoaded &&
      <Form
        id={'everyday-rewards-form'}
        schema={{}}
      >
        {!everydayRewardsLinked &&
          <div>
            You do not have a linked Everyday Rewards card at the moment.
          </div>
        }

        {everydayRewardsLinked &&
          <div>
            <div>
              Your account is currently linked to an Everyday Rewards card.
            </div>
            <EverydayRewardsCardNumber value={cardNumber} />
          </div>
        }

        <div className="everyday-rewards__fields">
          <div className="everyday-rewards__logo">
            <img src={everydayRewardsLogo} className="everyday-rewards__everyday-rewards-logo" />
          </div>
          <div className="everyday-rewards__actions">
            {!everydayRewardsLinked &&
              <Button
                color={ButtonColor.Blue}
                label="Link"
                onClick={readOnly ? '' : onLinkToEverydayRewardsClick}
                variant={ButtonVariant.Primary}
                inactive={readOnly}
              />
            }
            {everydayRewardsLinked &&
              <Button
                color={ButtonColor.Red}
                label="Unlink"
                onClick={readOnly ? '' : onUnlinkFromEverydayRewardsClick}
                variant={ButtonVariant.Primary}
                inactive={everydayRewardsDisconnectLoading || readOnly}
              />
            }
          </div>
        </div>
      </Form>
    }

    {showEverydayRewardsHome && !everydayRewardsLoginUriLoaded &&
      <div>Loading...</div>
    }

    {showEverydayRewardsInProgress &&
      <EverydayRewardsInProgress
        onDoneClick={onInProgressCompleteClick}
      />
    }
  </>;
};

export default EverydayRewards;
